import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

export const queryKeys = {
  health: {
    check: () => ['health'] as const,
  },
  me: () => ['me'] as const,
  helpSupport: {
    all: ['helpSupport'] as const,
    list: (type?: string, search?: string) =>
      ['helpSupport', 'list', type ?? 'all', search ?? ''] as const,
    detail: (slug: string) => ['helpSupport', 'detail', slug] as const,
    admin: (filters?: object) => ['helpSupport', 'admin', filters ?? {}] as const,
    adminDetail: (id: string) => ['helpSupport', 'adminDetail', id] as const,
  },
  roles: {
    list: () => ['roles', 'list'] as const,
    users: (search?: string) => ['roles', 'users', search] as const,
  },
  members: {
    all: () => ['members'] as const,
    list: (filters?: Record<string, unknown>) => ['members', 'list', filters ?? {}] as const,
    detail: (id: string) => ['members', 'detail', id] as const,
    linkableUsers: (search?: string) => ['members', 'linkableUsers', search ?? ''] as const,
  },
  projects: {
    projects: () => ['projects'] as const,
    projectList: (filters?: Record<string, unknown>) =>
      ['projects', 'list', filters] as const,
    accessible: () => ['projects', 'accessible'] as const,
    projectDetail: (id: string) => ['projects', id] as const,
  },
  trades: {
    all: () => ['trades'] as const,
    list: (filters?: Record<string, unknown>) => ['trades', 'list', filters] as const,
    detail: (id: string) => ['trades', id] as const,
  },
  parties: {
    all: () => ['parties'] as const,
    list: (filters?: Record<string, unknown>) => ['parties', 'list', filters] as const,
    detail: (id: string) => ['parties', id] as const,
  },
  projectParties: {
    list: (projectId: string) => ['projectParties', projectId] as const,
    detail: (id: string) => ['projectParties', 'detail', id] as const,
  },
  schedule: {
    phases: (projectId: string) => ['schedule', 'phases', projectId] as const,
    phaseDetail: (id: string) => ['schedule', 'phase', id] as const,
    tasks: (phaseId: string) => ['schedule', 'tasks', phaseId] as const,
    projectTasks: (projectId: string) => ['schedule', 'projectTasks', projectId] as const,
    taskDetail: (id: string) => ['schedule', 'task', id] as const,
    milestones: (projectId: string) => ['schedule', 'milestones', projectId] as const,
  },
  finance: {
    budget: (projectId: string) => ['finance', 'budget', projectId] as const,
    budgetCategory: (id: string) => ['finance', 'budgetCategory', id] as const,
    pledges: (projectId?: string) =>
      projectId
        ? (['finance', 'pledges', projectId] as const)
        : (['finance', 'pledges'] as const),
    pledgeDetail: (id: string) => ['finance', 'pledge', id] as const,
    memberGiving: (memberId: string) => ['finance', 'memberGiving', memberId] as const,
    changeOrders: (projectId?: string) =>
      projectId
        ? (['finance', 'changeOrders', projectId] as const)
        : (['finance', 'changeOrders'] as const),
    changeOrderDetail: (id: string) => ['finance', 'changeOrder', id] as const,
    magicplanEstimateStatus: (projectId: string) =>
      ['finance', 'magicplanEstimateStatus', projectId] as const,
  },
  permits: {
    list: (projectId: string) => ['permits', 'list', projectId] as const,
    detail: (id: string) => ['permits', 'detail', id] as const,
    certificates: (projectId: string) => ['permits', 'certificates', projectId] as const,
    compliance: (projectId?: string) => ['permits', 'compliance', projectId ?? 'all'] as const,
  },
  fieldOps: {
    rfis: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['fieldOps', 'rfis', filters] as const)
        : (['fieldOps', 'rfis'] as const),
    dailyLogs: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['fieldOps', 'dailyLogs', filters] as const)
        : (['fieldOps', 'dailyLogs'] as const),
    punchList: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['fieldOps', 'punchList', filters] as const)
        : (['fieldOps', 'punchList'] as const),
  },
  volunteers: {
    shifts: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['volunteers', 'shifts', filters] as const)
        : (['volunteers', 'shifts'] as const),
    shiftDetail: (id: string) => ['volunteers', 'shift', id] as const,
  },
  ministrySpaces: {
    list: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['ministrySpaces', 'list', filters] as const)
        : (['ministrySpaces', 'list'] as const),
    detail: (id: string) => ['ministrySpaces', 'detail', id] as const,
  },
  community: {
    prayer: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['community', 'prayer', filters] as const)
        : (['community', 'prayer'] as const),
    faqs: (projectId: string) => ['community', 'faqs', projectId] as const,
    missionProjects: (projectId: string) => ['community', 'missionProjects', projectId] as const,
    communications: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['community', 'communications', filters] as const)
        : (['community', 'communications'] as const),
    decisionJournal: (projectId?: string) =>
      projectId ? (['community', 'decisionJournal', projectId] as const) : (['community', 'decisionJournal'] as const),
  },
  activity: {
    links: (entityType: string, entityId: string) =>
      ['activity', 'links', entityType, entityId] as const,
    timeline: (projectId?: string, filters?: Record<string, unknown>) =>
      projectId
        ? (['activity', 'timeline', projectId, filters ?? {}] as const)
        : (['activity', 'timeline'] as const),
  },
  messaging: {
    conversations: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['messaging', 'conversations', filters] as const)
        : (['messaging', 'conversations'] as const),
    conversation: (id: string) => ['messaging', 'conversation', id] as const,
    messages: (conversationId: string) => ['messaging', 'messages', conversationId] as const,
  },
  dashboard: {
    metrics: () => ['dashboard', 'metrics'] as const,
    construction: (projectId?: string) =>
      projectId ? (['dashboard', 'construction', projectId] as const) : (['dashboard', 'construction'] as const),
  },
  floorPlans: {
    list: (projectId: string) => ['floorPlans', 'list', projectId] as const,
    status: () => ['floorPlans', 'status'] as const,
    provider: () => ['floorPlans', 'provider'] as const,
    link: (projectId: string) => ['floorPlans', 'link', projectId] as const,
  },
  estimating: {
    spaceTypes: () => ['estimating', 'spaceTypes'] as const,
    scopeCategories: () => ['estimating', 'scopeCategories'] as const,
    spaces: (projectId: string, filters?: Record<string, unknown>) =>
      ['estimating', 'spaces', projectId, filters] as const,
    spacesByProject: (projectId: string) => ['estimating', 'spaces', projectId] as const,
    scopeItems: (projectId: string, filters?: Record<string, unknown>) =>
      ['estimating', 'scopeItems', projectId, filters] as const,
    scopeItemsByProject: (projectId: string) => ['estimating', 'scopeItems', projectId] as const,
    scopeItem: (id: string) => ['estimating', 'scopeItem', id] as const,
    workPackages: (projectId: string) => ['estimating', 'workPackages', projectId] as const,
    // Economics (VS Phase 8)
    materialCatalog: (filters?: Record<string, unknown>) =>
      ['estimating', 'materialCatalog', filters] as const,
    materialRequirements: (scopeItemId: string) =>
      ['estimating', 'materialRequirements', scopeItemId] as const,
    fixtures: (projectId: string, scopeItemId?: string) =>
      scopeItemId === undefined
        ? (['estimating', 'fixtures', projectId] as const)
        : (['estimating', 'fixtures', projectId, scopeItemId] as const),
    tradeRequirements: (scopeItemId: string) =>
      ['estimating', 'tradeRequirements', scopeItemId] as const,
    laborEstimates: (scopeItemId: string) => ['estimating', 'laborEstimates', scopeItemId] as const,
    costItems: (projectId: string, filters?: Record<string, unknown>) =>
      ['estimating', 'costItems', projectId, filters] as const,
    costItemsByProject: (projectId: string) => ['estimating', 'costItems', projectId] as const,
    budgetReconciliation: (projectId: string) =>
      ['estimating', 'budgetReconciliation', projectId] as const,
    approvals: (projectId: string, filters?: Record<string, unknown>) =>
      ['estimating', 'approvals', projectId, filters] as const,
    approvalsByProject: (projectId: string) => ['estimating', 'approvals', projectId] as const,
    designDeltas: (projectId: string, filters?: Record<string, unknown>) =>
      ['estimating', 'designDeltas', projectId, filters] as const,
    designDeltasByProject: (projectId: string) => ['estimating', 'designDeltas', projectId] as const,
  },
  procurement: {
    bids: (projectId: string, filters?: Record<string, unknown>) =>
      ['procurement', 'bids', projectId, filters] as const,
    bidsByProject: (projectId: string) => ['procurement', 'bids', projectId] as const,
    purchaseOrders: (projectId: string, filters?: Record<string, unknown>) =>
      ['procurement', 'purchaseOrders', projectId, filters] as const,
    purchaseOrdersByProject: (projectId: string) =>
      ['procurement', 'purchaseOrders', projectId] as const,
  },
  reporting: {
    bom: (projectId: string) => ['reporting', 'bom', projectId] as const,
    labor: (projectId: string) => ['reporting', 'labor', projectId] as const,
    permitMatrix: (projectId: string) => ['reporting', 'permitMatrix', projectId] as const,
    scopeStatus: (projectId: string) => ['reporting', 'scopeStatus', projectId] as const,
    schedule: (projectId: string) => ['reporting', 'schedule', projectId] as const,
    costSummary: (projectId: string) => ['reporting', 'costSummary', projectId] as const,
  },
  engagement: {
    contacts: (filters?: Record<string, unknown>) => ['engagement', 'contacts', filters] as const,
    contact: (id: string) => ['engagement', 'contact', id] as const,
    audiences: () => ['engagement', 'audiences'] as const,
    audience: (id: string) => ['engagement', 'audience', id] as const,
    audiencePreview: (id: string) => ['engagement', 'audience', id, 'preview'] as const,
    templates: (channel?: string) => ['engagement', 'templates', channel ?? 'all'] as const,
    template: (id: string) => ['engagement', 'template', id] as const,
    campaigns: (filters?: Record<string, unknown>) => ['engagement', 'campaigns', filters] as const,
    campaign: (id: string) => ['engagement', 'campaign', id] as const,
    integrationStatus: () => ['engagement', 'integrationStatus'] as const,
  },
  // The kiosk is tenant-wide (one per deployment), so nothing here is keyed by project.
  kiosk: {
    view: (token: string) => ['kiosk', 'view', token] as const,
    /** Cheap change probe the display polls between full view fetches. */
    revision: (token: string) => ['kiosk', 'revision', token] as const,
    adminConfig: () => ['kiosk', 'admin'] as const,
    milestonePhotos: () => ['kiosk', 'milestonePhotos'] as const,
    mediaOptions: () => ['kiosk', 'mediaOptions'] as const,
  },
  media: {
    /** `null` is the church-wide scope: albums belonging to no project. */
    albums: (projectId: string | null) => ['media', 'albums', projectId ?? 'church'] as const,
    assets: (albumId: string) => ['media', 'assets', albumId] as const,
  },
  attachments: {
    /** Everything filed against one object -- the panel. */
    forEntity: (entityType: string, entityId: string) =>
      ['attachments', 'entity', entityType, entityId] as const,
    /**
     * One list page's worth, batched. `ids` must already be sorted so a
     * re-render that reorders rows does not read as a different query.
     */
    batch: (entityType: string, ids: readonly string[]) =>
      ['attachments', 'batch', entityType, ids.join(',')] as const,
    /** The Files page. `unfiled` is its own tab, not a filter on the same key. */
    list: (projectId: string | null, filters?: Record<string, unknown>) =>
      ['attachments', 'list', projectId ?? 'none', filters ?? {}] as const,
    unfiled: (projectId: string | null) => ['attachments', 'unfiled', projectId ?? 'none'] as const,
    detail: (id: string) => ['attachments', 'detail', id] as const,
    /**
     * Candidates for the attach picker, fetched from each owning module's own
     * list endpoint over HTTP. Keyed by type + project so switching the type in
     * the picker does not refetch the previous one's list.
     */
    candidates: (entityType: string, projectId: string | null) =>
      ['attachments', 'candidates', entityType, projectId ?? 'none'] as const,
  },
  /**
   * ONE KEY. The unread count rides in the list payload rather than a second query, so a
   * mutation invalidates exactly one thing and the badge and the panel cannot disagree.
   */
  notifications: {
    list: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['notifications', filters] as const)
        : (['notifications'] as const),
  },
  // SmartChurch (facilities / smart devices). Devices are CHURCH-WIDE, so nothing
  // here is keyed by project. `devices`, `areas` and `alerts` return the bare prefix
  // when unfiltered so ONE prefix invalidation from a mutation matches every filtered
  // variant. (The state probe deliberately invalidates only `dashboard`.)
  smartChurch: {
    gateways: () => ['smartChurch', 'gateways'] as const,
    gateway: (id: string) => ['smartChurch', 'gateway', id] as const,
    status: () => ['smartChurch', 'status'] as const,
    dashboard: () => ['smartChurch', 'dashboard'] as const,
    devices: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['smartChurch', 'devices', filters] as const)
        : (['smartChurch', 'devices'] as const),
    device: (id: string) => ['smartChurch', 'device', id] as const,
    /** The flat rule-subject list. Filters mirror `devices` so the two read alike. */
    entities: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['smartChurch', 'entities', filters] as const)
        : (['smartChurch', 'entities'] as const),
    areas: (gatewayId?: string) =>
      gatewayId
        ? (['smartChurch', 'areas', gatewayId] as const)
        : (['smartChurch', 'areas'] as const),
    history: (entityId: string, window: string) =>
      ['smartChurch', 'history', entityId, window] as const,
    commands: (deviceId: string) => ['smartChurch', 'commands', deviceId] as const,
    alerts: (filters?: Record<string, unknown>) =>
      filters && Object.keys(filters).length > 0
        ? (['smartChurch', 'alerts', filters] as const)
        : (['smartChurch', 'alerts'] as const),
    schedules: () => ['smartChurch', 'schedules'] as const,
    rules: () => ['smartChurch', 'rules'] as const,
    discovery: (gatewayId: string) => ['smartChurch', 'discovery', gatewayId] as const,
  },
  branding: () => ['branding'] as const,
  publicTheme: () => ['publicTheme'] as const,
  publicTranslations: () => ['publicTranslations'] as const,
  features: () => ['features'] as const,
  settings: {
    detail: () => ['settings'] as const,
    theme: () => ['settings', 'theme'] as const,
    translations: () => ['settings', 'translations'] as const,
    languages: () => ['settings', 'languages'] as const,
    availableLanguages: () => ['settings', 'availableLanguages'] as const,
    integrations: () => ['settings', 'integrations'] as const,
    measurement: () => ['settings', 'measurement'] as const,
    siteAccess: () => ['settings', 'siteAccess'] as const,
  },
} as const;
